import React, { useState, useEffect } from 'react';
import { Briefcase, Plus, TrendingUp, DollarSign, Users, Search, Filter } from 'lucide-react';
import { apiService, Portfolio } from '../services/api';
import PortfolioCard from '../components/PortfolioCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useNavigate } from 'react-router-dom';

const Portfolios: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSentiment, setFilterSentiment] = useState<string>('all');
  const navigate = useNavigate();

  const fetchPortfolios = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPortfolios();
      setPortfolios(response.portfolios);
    } catch (err) {
      setError('Failed to load portfolios. Please try again.');
      console.error('Error fetching portfolios:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolios();
  }, []);

  // Filter portfolios based on search term and sentiment
  const filteredPortfolios = portfolios.filter(portfolio => {
    const matchesSearch = portfolio.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         portfolio.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         portfolio.phrase_analysis.phrase.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSentiment = filterSentiment === 'all' || 
                            portfolio.phrase_analysis.sentiment.toLowerCase() === filterSentiment.toLowerCase();
    
    return matchesSearch && matchesSentiment;
  });

  // Calculate statistics
  const totalPortfolios = portfolios.length;
  const positivePortfolios = portfolios.filter(p => p.phrase_analysis.sentiment === 'positive').length;
  const negativePortfolios = portfolios.filter(p => p.phrase_analysis.sentiment === 'negative').length;
  const neutralPortfolios = portfolios.filter(p => p.phrase_analysis.sentiment === 'neutral').length;

  if (loading) {
    return (
      <div className="space-y-2">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Portfolios</h1>
              <p className="text-gray-600">Manage your investment portfolios</p>
            </div>
          </div>
        </div>
        
        <LoadingSpinner size="lg" text="Loading portfolios..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Portfolios</h1>
              <p className="text-gray-600">Manage your investment portfolios</p>
            </div>
          </div>
        </div>
        
        <ErrorMessage message={error} onRetry={fetchPortfolios} />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Briefcase className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Portfolios</h1>
            <p className="text-gray-600">Manage your investment portfolios</p>
          </div>
        </div>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
          onClick={() => navigate('/dashboard/domain-assistant')}
        >
          <Plus className="h-4 w-4" />
          <span>New Portfolio</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Portfolios</p>
              <p className="text-2xl font-bold text-gray-900">{totalPortfolios}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <TrendingUp className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Positive Sentiment</p>
              <p className="text-2xl font-bold text-green-600">{positivePortfolios}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Negative Sentiment</p>
              <p className="text-2xl font-bold text-red-600">{negativePortfolios}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Users className="h-5 w-5 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Neutral Sentiment</p>
              <p className="text-2xl font-bold text-gray-600">{neutralPortfolios}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search portfolios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterSentiment}
              onChange={(e) => setFilterSentiment(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Sentiments</option>
              <option value="positive">Positive</option>
              <option value="negative">Negative</option>
              <option value="neutral">Neutral</option>
            </select>
          </div>
        </div>
      </div>

      {/* Portfolio List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Your Portfolios ({filteredPortfolios.length})
          </h2>
          <p className="text-gray-600 mt-1">
            {filteredPortfolios.length === 0 
              ? 'No portfolios found matching your criteria.'
              : 'Manage and track your investment portfolios'
            }
          </p>
        </div>
        
        <div className="p-2">
          {filteredPortfolios.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No portfolios found</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Try adjusting your search terms or filters to find portfolios.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
              {filteredPortfolios.map((portfolio) => (
                <PortfolioCard key={portfolio.id} portfolio={portfolio} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Portfolios; 